import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Reveal from "@/components/sites/Reveal";
import EstatePropertyCard from "@/components/sites/EstatePropertyCard";
import { EstateHeader, EstateFooter, EstateStickyCta } from "@/components/sites/EstateChrome";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";
import { imageSrcSet, SIZES } from "@/lib/responsiveImage";
import { fmtPrice } from "@/lib/terms";
import { withPreview } from "@/lib/sitePreview";
import { leadWithArea, useVisitInterest } from "@/lib/visitInterest";
import { areaCaption } from "@/lib/agencyDetails";
import PreviewBadge from "@/components/sites/PreviewBadge";
import AgencyReviews from "@/components/sites/AgencyReviews";

/**
 * The agency home page, in the design pack's "Estate" theme.
 *
 * THE RULE THIS PAGE IS WRITTEN TO
 * --------------------------------
 * Every word on it comes from the agency's own record, from the listing
 * records, or is a figure computed from them. The design pack uses
 * Windlebrook as its reference and therefore contains Windlebrook's WORDS:
 * "Homes sold with care, not noise", four named process steps, three review
 * quotes, four market statistics. None of those are here. Taking them would
 * hand the next agency Windlebrook's voice and, in the case of the
 * statistics, numbers about somebody else's business.
 *
 * So each section renders only if the record carries what it needs, and a
 * sparse agency gets a shorter page rather than a padded one. That is not a
 * compromise: a page with four invented statistics on it is worse than a
 * page with none.
 */
export default function EstateHome({ preview = true }) {
  const brand = useBrand();
  const market = useMarket();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  // Whether the two requests below have SETTLED, win or lose. Distinct from
  // "listings is empty", which is also true for the moment before they answer
  // — and the page has to tell those apart, because a sparse agency getting
  // no properties section and a rich one whose properties have not arrived
  // yet look identical until this is true.
  const [loaded, setLoaded] = useState(false);
  const [postcode, setPostcode] = useState("");
  const p = (href) => withPreview(href, preview);

  useEffect(() => {
    if (!tenantKnown) return undefined;
    let stale = false;
    setLoaded(false);
    Promise.all([
      api.get(`/crm/public/properties?tenant=${tenantSlug}&limit=7`).then((r) => r.data).catch(() => null),
      api.get(`/crm/public/stats?tenant=${tenantSlug}`).then((r) => r.data).catch(() => null),
    ]).then(([l, s]) => {
      if (stale) return;
      // All three together: the sections below key off `loaded`, and setting
      // it in a later microtask would paint them in two steps.
      setListings(l?.items || []);
      setStats(s || null);
      setLoaded(true);
    }).catch(() => { if (!stale) setLoaded(true); });
    return () => { stale = true; };
  }, [tenantSlug, tenantKnown]);

  const areas = brand.areas_served || [];
  const faq = brand.faq || [];
  const badges = brand.trust_badges || [];
  const testimonial = brand.testimonial || null;
  const heroImage = brand.assets?.hero_image_url;

  /**
   * The pulse strip, and only figures we can stand behind.
   *
   * The design shows average sold price, days to sold STC, percentage of
   * asking achieved and registered buyers. Three of those are in no record
   * this site can read: there is no Land Registry report built for most
   * agencies, and the other two are CRM performance figures a website-only
   * customer does not have. Inventing them is the one thing this page must
   * not do, so the strip carries what is true — what is on the market, the
   * average asking price of the homes on the books, what has sold, and how
   * many areas are covered — or it does not render at all.
   */
  const pulse = useMemo(() => {
    const out = [];
    if (stats && stats.on_market) {
      out.push({ k: "On the market", v: String(stats.on_market), d: "Available to view now" });
    }
    const priced = listings.filter((l) => l.listing_type !== "letting" && l.asking_price > 0);
    if (priced.length >= 3) {
      const avg = Math.round(priced.reduce((n, l) => n + l.asking_price, 0) / priced.length);
      out.push({ k: "Average asking price", v: fmtPrice(avg, market),
                 d: `Across ${priced.length} homes on our books` });
    }
    if (stats && stats.sold_or_let) {
      out.push({ k: "Sold or let", v: String(stats.sold_or_let), d: "Recently, through us" });
    }
    if (areas.length) {
      out.push({ k: "Areas we cover", v: String(areas.length),
                 d: brand.commuter_belt || areas.slice(0, 3).join(", ") });
    }
    return out.slice(0, 4);
  }, [stats, listings, areas, market, brand.commuter_belt]);

  // Selling and letting are the two the record actually describes, each with
  // the agency's own headline and sub. Registering is the third thing a
  // visitor can do, and its line is the only sentence on this page that is
  // not the agency's, because there is nowhere on the record for it and it
  // means the same thing for every agency.
  const services = useMemo(() => [
    brand.selling && brand.selling.headline ? {
      to: tenantPath("sell"), title: "Selling",
      line: brand.selling.sub || brand.selling.intro, cta: "Sell with us",
    } : null,
    brand.letting && brand.letting.headline ? {
      to: tenantPath("let"), title: "Letting",
      line: brand.letting.sub || brand.letting.promo || brand.letting.intro, cta: "Let with us",
    } : null,
    {
      to: tenantPath("register"), title: "Buying",
      line: "Tell us what you are looking for and hear about homes as they come to us.",
      cta: "Register",
    },
  ].filter(Boolean), [brand.selling, brand.letting, tenantPath]);

  // The design's four process steps. `why_us` is the agency's own list of
  // four reasons: the same shape, in their words.
  const steps = ((brand.selling || {}).why_us || []).slice(0, 4);

  // LEAD WITH WHAT THEY HAVE BEEN READING, within this visit.
  //
  // Somebody who has just read the Windlesham area guide and clicked back
  // here should see Windlesham houses first. The order changes; nothing is
  // hidden, because an agency with four houses must not look like an agency
  // with one. If they arrived cold, or it is a crawler, `topArea` is null
  // and this is the page it has always been.
  const { topArea, forget } = useVisitInterest();
  const { rows: ordered, moved } = useMemo(
    () => leadWithArea(listings, topArea?.key), [listings, topArea]);
  const leading = moved > 0 ? topArea : null;
  const feature = ordered[0];
  const rest = ordered.slice(1, 5);
  const listingHref = (l) => p(`${tenantPath("properties")}/${l.website_slug || l.id}`);

  const submitPostcode = (e) => {
    e.preventDefault();
    const q = postcode.trim();
    navigate(p(tenantPath(`valuation${q ? `?postcode=${encodeURIComponent(q)}` : ""}`)));
  };

  return (
    <div className="fs" data-testid="estate-home">
      <SeoHead
        title={`${biz.name}${areas.length ? `, Estate Agents in ${areas.slice(0, 2).join(" & ")}` : ""}`}
        description={biz.elevator_pitch || biz.hero_sub}
        image={heroImage ? mediaUrl(heroImage) : undefined}
      />
      <a className="skip" href="#main">Skip to content</a>
      <EstateHeader dark preview={preview} />

      {/* Nothing here paints on a guess about what is coming.

          Two things land after the first paint and both used to move the
          page. The hero is 124px taller once /brand answers — it gains the
          commuter-belt eyebrow, the agency's own headline in place of the
          default one, their sub-line and their badges — and the featured
          properties are a SECOND request that inserts 2,427px above
          everything below it. Measured together: CLS 0.083, on a section
          sitting at y=791 in an 812px viewport, which is exactly close
          enough to the fold to be on screen when it gets shoved off it.

          So the hero waits for the brand and the rest waits for the
          listings, and each paints once, at its final geometry. The screen
          stays reserved between the two for the reason the listings page
          gives at length: a hero and nothing else is barely a screen tall,
          and letting it collapse to that brings the footer up into view to
          be pushed down again a moment later. */}
      <main id="main" style={{ minHeight: "100vh" }}>
        {brand.brand_resolved && (
          <section className="hero">
            <div className="hero-media">
              {heroImage && (
                <img src={mediaUrl(heroImage)} alt={brand.assets?.hero_caption || ""}
                     srcSet={imageSrcSet({ url: heroImage, widths: brand.assets?.hero_widths })}
                     sizes={SIZES.full}
                     fetchPriority="high" decoding="async" />
              )}
            </div>
            <div className="hero-scrim" />
            <div className="container hero-body">
              <div>
                {brand.commuter_belt && (
                  <div className="eyebrow" style={{ color: "var(--accent)" }}>{brand.commuter_belt}</div>
                )}
                <h1 className="display-xl" style={{ marginTop: 22 }}>
                  {biz.hero_headline || biz.name}
                </h1>
                {biz.hero_sub && <p className="lede" style={{ marginTop: 22 }}>{biz.hero_sub}</p>}
                {badges.length > 0 && (
                  <div className="hero-meta">
                    {badges.map((b) => <span key={b.text}>{b.text}</span>)}
                  </div>
                )}
              </div>
              <div>
                <form className="val-bar" onSubmit={submitPostcode}>
                  <div>
                    <label htmlFor="fs-pc">Instant valuation</label>
                    <input id="fs-pc" name="postcode" value={postcode} autoComplete="postal-code"
                           onChange={(e) => setPostcode(e.target.value)}
                           placeholder="Enter your postcode" data-testid="estate-hero-postcode" />
                  </div>
                  <button className="btn" type="submit">
                    Value my home <span className="arrow" aria-hidden="true">&rarr;</span>
                  </button>
                </form>
                <p className="val-note">60 seconds · HM Land Registry data · No obligation.</p>
              </div>
            </div>
          </section>
        )}

        {brand.brand_resolved && loaded && (<>
          {brand.show_stats_strip !== false && pulse.length >= 2 && (
            <section className="pulse" data-testid="estate-pulse">
              <div className="container pulse-row">
                {pulse.map((s) => (
                  <div className="pulse-item" key={s.k}>
                    <div className="k">{s.k}</div>
                    <div className="v num">{s.v}</div>
                    <div className="d">{s.d}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {listings.length > 0 && (
            <section className="section" id="properties">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">On the market</div>
                    <h2 className="display-l" style={{ marginTop: 14 }}>
                      A considered <span className="serif-i">selection</span>
                    </h2>
                  </div>
                  <Link className="link" to={p(tenantPath("properties"))}>
                    {stats && stats.on_market ? `All ${stats.on_market} properties` : "All properties"}
                    <span className="arrow" aria-hidden="true">&rarr;</span>
                  </Link>
                </Reveal>
                {/* A page that quietly rearranges itself is unsettling. If
                    the order changed, it says so, and says how to undo it. */}
                {leading && (
                  <p className="note" data-testid="estate-leading-with"
                     style={{ marginTop: -12, marginBottom: 20 }}>
                    Showing {leading.label} first, because that is what you were looking at.{" "}
                    <button type="button" onClick={forget}
                            style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>
                      Show the usual order
                    </button>
                  </p>
                )}
                <div className="grid cols-4">
                  {feature && (
                    <EstatePropertyCard property={feature} market={market} feature
                                        href={listingHref(feature)} />
                  )}
                  {rest.map((l) => (
                    <EstatePropertyCard key={l.id} property={l} market={market} href={listingHref(l)} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {(testimonial && testimonial.quote) || biz.elevator_pitch ? (
            <section className="section tint">
              <div className="container">
                <Reveal className="split">
                  {heroImage && (
                    <figure className="media plate">
                      <div className="shot">
                        <img src={mediaUrl(heroImage)} alt={brand.assets?.hero_caption || ""} loading="lazy" />
                      </div>
                      {brand.assets?.hero_caption && (
                        <figcaption className="figcap">
                          <span className="what">{brand.assets.hero_caption}</span>
                        </figcaption>
                      )}
                    </figure>
                  )}
                  <div>
                    {/* THE SECTION THAT HAD NO HEADING.

                        This band is the tallest on the page after the listings
                        and it used to open on an eyebrow and go straight into
                        a paragraph, so it read as a photograph with some words
                        beside it rather than as a section of the document.

                        The tagline moved here from the eyebrow, which is where
                        it belongs: it is the one sentence the agency has
                        written about itself, and set at display size in their
                        own face it is a heading. It was also the eyebrow of
                        the closing call to action, so the same six words
                        opened two of the page's ten sections. Now it appears
                        once, and the eyebrow says which section this is. */}
                    <div className="eyebrow">About {biz.short_name || biz.name}</div>
                    {biz.tagline && (
                      <h2 className="display-l" style={{ marginTop: 14 }}>{biz.tagline}</h2>
                    )}
                    {biz.elevator_pitch && (
                      <p className="lede" style={{ marginTop: 22 }}>{biz.elevator_pitch}</p>
                    )}
                    {testimonial && testimonial.quote && (
                      <>
                        <blockquote style={{ marginTop: 28 }}>&ldquo;{testimonial.quote}&rdquo;</blockquote>
                        {testimonial.attribution && <cite>{testimonial.attribution}</cite>}
                      </>
                    )}
                    {pulse.length >= 3 && (
                      <div className="stats">
                        {pulse.slice(0, 3).map((s) => (
                          <div key={s.k}>
                            <div className="v num">{s.v}</div>
                            <div className="k">{s.k}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Reveal>
              </div>
            </section>
          ) : null}

          {services.length > 0 && (
            <section className="section">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">What we do</div>
                    <h2 className="display-l" style={{ marginTop: 14 }}>
                      How we can <span className="serif-i">help</span>
                    </h2>
                  </div>
                </Reveal>
                <div className="svc">
                  {services.map((s, i) => (
                    <Reveal className="svc-item" key={s.title}>
                      <div className="n num">{String(i + 1).padStart(2, "0")}</div>
                      <h3>{s.title}</h3>
                      {s.line && <p>{s.line}</p>}
                      <Link className="link" to={p(s.to)}>
                        {s.cta} <span className="arrow" aria-hidden="true">&rarr;</span>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>
          )}

          {steps.length > 0 && (
            <section className="section on-ink">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">How we work</div>
                    <h2 className="display-l" style={{ marginTop: 14 }}>
                      {(brand.selling || {}).headline || "What you can expect"}
                    </h2>
                  </div>
                  <Link className="link" to={p(tenantPath("valuation"))}>
                    Book a valuation <span className="arrow" aria-hidden="true">&rarr;</span>
                  </Link>
                </Reveal>
                <div className="steps">
                  {steps.map((s, i) => (
                    <div className="step" key={i}>
                      <div className="n num">{i + 1}</div>
                      <p>{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {areas.length > 0 && (
            <section className="section">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">Where we work</div>
                    <h2 className="display-l" style={{ marginTop: 14 }}>
                      {areas.length} areas, <span className="serif-i">one team</span>
                    </h2>
                  </div>
                  <Link className="link" to={p(tenantPath("guides"))}>
                    All area guides <span className="arrow" aria-hidden="true">&rarr;</span>
                  </Link>
                </Reveal>
                <div className="rail">
                  {areas.map((a) => {
                    // The design shows a short fact under each area name. The
                    // record holds an area GUIDE, which is several paragraphs,
                    // and its first sentence can run to forty words. Trimmed at
                    // a word boundary so the rail reads as captions rather than
                    // as nine paragraphs of different lengths. The agency's own
                    // words either way: the full guide is one click away.
                    const firstSentence = areaCaption((brand.area_guides || {})[a]);
                    return (
                      <Link className="area" key={a}
                            to={p(tenantPath(`area/${a.toLowerCase().replace(/\s+/g, "-")}`))}>
                        <div className="body">
                          <h3>{a}</h3>
                          {firstSentence && <div className="k">{firstSentence}.</div>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {testimonial && testimonial.quote && (
            <section className="section tint">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">In their words</div>
                    <h2 className="display-l" style={{ marginTop: 14 }}>
                      What our clients <span className="serif-i">say</span>
                    </h2>
                  </div>
                </Reveal>
                <div className="reviews one">
                  <Reveal className="review">
                    <div className="stars" aria-label="Five stars">★★★★★</div>
                    <p>&ldquo;{testimonial.quote}&rdquo;</p>
                    {testimonial.attribution && <div className="who">{testimonial.attribution}</div>}
                  </Reveal>
                </div>
              </div>
            </section>
          )}

          {/* Google's rating and reviews, and quotes the agency's own
              clients agreed to. Renders nothing when there are neither. */}
          <AgencyReviews variant="estate" />

          {faq.length > 0 && (
            <section className="section">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">Questions</div>
                    <h2 className="display-l" style={{ marginTop: 14 }}>Frequently asked</h2>
                  </div>
                </Reveal>
                <div className="faq">
                  {faq.map((f) => (
                    <details key={f.question}>
                      <summary>{f.question}</summary>
                      <div className="a">{f.answer}</div>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="section cta on-ink">
            <div className="container">
              <div>
                <div className="eyebrow">Thinking of moving?</div>
                <h2 className="display-l" style={{ marginTop: 18 }}>
                  Start with an <span className="serif-i">honest number</span>.
                </h2>
              </div>
              <div className="cta-actions">
                <Link className="btn" to={p(tenantPath("valuation"))}>
                  Instant valuation <span className="arrow" aria-hidden="true">&rarr;</span>
                </Link>
                <Link className="btn ghost" to={p(tenantPath("register"))}>Register as a buyer</Link>
              </div>
            </div>
          </section>
        </>)}
      </main>

      <EstateFooter preview={preview} />
      <EstateStickyCta preview={preview} />
      {preview && <PreviewBadge />}
    </div>
  );
}
